import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { groupPosition, groupRisk, groupTint, TRADER_TAG_INFO, type GroupRiskLevel, type TerminalTrade, type TraderTag, type WalletGroup } from '@pleco/shared';
import { fmtAge, fmtNum, fmtPct, fmtSol, fmtUsd, shortAddr, solscanTx } from '../../lib/fmt.js';
import { terminalApi } from '../../lib/terminalApi.js';
import { useMapStore } from '../../store/mapStore.js';
import { LEVEL_LABEL, type Verdict } from '../../lib/verdict.js';
import { groupById, useTerminal } from '../../store/terminalStore.js';
import { IconClose, IconFilter, IconMap, IconSearch } from '../Icons.js';

/** Supply share with one decimal; tiny non-zero amounts read "<0.1%", not "0.0%". */
const fmtYears = (d: number) => (d >= 365 ? `${(d / 365).toFixed(d >= 730 ? 0 : 1)} years` : d >= 60 ? `${Math.round(d / 30)} months` : `${Math.round(d)} days`);

export const pct = (v: number) => (v > 0 && v < 0.05 ? '<0.1%' : fmtPct(v, 1));

/** Group names from the engine carry addresses ("Shared funder AFJg…bHwu");
 *  show what the group *is* instead. Bundles keep their timing in the name. */
const GROUP_TITLE: Record<WalletGroup['kind'], string> = {
  dev_network: "Creator's wallets",
  bundle: 'Launch bundle',
  funder: 'Same funding wallet',
  funding_tree: 'Same source of money',
  direct_funding: 'Funded by one trader',
  distribution: 'Tokens handed out',
  cex_batch: 'Exchange batch',
  wallet_farm: 'Wallet farm',
  gas_sponsored: 'Gas-sponsored wallets',
};
export const groupTitle = (g: WalletGroup) => (g.kind === 'bundle' ? g.name.replace(/^Bundle buy/, 'Bundle') : GROUP_TITLE[g.kind] ?? g.name);

/* ------------------------------------------------------------------ verdict */

export function VerdictCard({ v, onGroup }: { v: Verdict; onGroup: (id: string) => void }) {
  const analysis = useTerminal((s) => s.analysis);
  const p = analysis?.progress;
  return (
    <section className={`tm-verdict lvl-${v.level ?? 'unknown'}`} aria-live="polite">
      <div className="tm-verdict-top">
        {v.level && <span className="tm-level">{LEVEL_LABEL[v.level]}</span>}
        {v.checking && (
          <span className="tm-verdict-checking">
            <span className="spinner" />
            {p?.toTrace ? `Checking who funded whom · ${p.traced}/${p.toTrace} wallets` : 'Checking who funded whom'}
          </span>
        )}
      </div>
      <h2>{v.headline}</h2>
      {v.maturity.level === 'established' && (
        <p className="tm-verdict-sub tm-established">
          <span className="tm-est-chip">Established token</span>
          {v.maturity.ageDays !== undefined && <>{fmtYears(v.maturity.ageDays)} old · </>}
          big market cap and real daily volume. Concentrated supply is common here (team, treasury, exchanges), so we flag live risks like linked
          wallets selling, not the holder layout alone.
        </p>
      )}
      {!v.level && v.checking && (
        <p className="tm-verdict-sub">New tokens take up to a minute. The map is live already, and colours appear as linked wallets are found.</p>
      )}
      {v.level === 'low' && v.reasons.length === 0 && (
        <p className="tm-verdict-sub">
          The ten largest wallets hold {pct(v.top10)}
          {v.groupsHeld > 0
            ? `, and linked wallets hold ${pct(v.groupsHeld)}, well below the level that signals a coordinated dump.`
            : ', and no linked wallets were found.'}{' '}
          Low risk is not no risk: still check the chart and liquidity before buying.
        </p>
      )}
      {v.reasons.length > 0 && (
        <ul className="tm-reasons">
          {v.reasons.map((r, i) => (
            <li key={i} className={`lvl-${r.level}`}>
              {r.title !== v.headline && <b>{r.title}. </b>}
              {r.text}
              {r.groupId && (
                <>
                  {' '}
                  <button className="tm-linkish" onClick={() => onGroup(r.groupId!)}>
                    Show group
                  </button>
                </>
              )}
            </li>
          ))}
        </ul>
      )}
      <div className="tm-kpis">
        <div>
          <em>Top 10 hold</em>
          <b className={v.top10 >= 50 ? 'bad' : v.top10 >= 30 ? 'warn' : ''}>{pct(v.top10)}</b>
        </div>
        <div>
          <em>Linked hold</em>
          <b className={v.groupsHeld >= 20 ? 'bad' : v.groupsHeld >= 8 ? 'warn' : ''}>{analysis ? pct(v.groupsHeld) : '…'}</b>
        </div>
        <div>
          <em>Holders</em>
          <b>{v.holders.toLocaleString()}</b>
        </div>
      </div>
    </section>
  );
}

/* ------------------------------------------------------------------ wallet name */

const TAG_PRIORITY: TraderTag[] = ['dev', 'insider', 'launch_bundle', 'farm_wallet', 'coordinated_sell', 'sniper', 'fresh_wallet', 'exited'];

export function WalletName({ address, label, tags, groupId }: { address: string; label?: string; tags?: TraderTag[]; groupId?: string }) {
  const analysis = useTerminal((s) => s.analysis);
  const g = groupById(analysis, groupId ?? analysis?.wallets[address]?.groupId);
  const all = tags ?? analysis?.wallets[address]?.tags ?? [];
  const tag = TAG_PRIORITY.find((t) => all.includes(t));
  const info = tag ? TRADER_TAG_INFO[tag] : undefined;
  return (
    <span className="tm-wname">
      <i className="tm-dot" style={{ background: g ? groupTint(g) : 'var(--bubble-solo)' }} />
      <span className={label ? '' : 'mono'}>{label ?? shortAddr(address)}</span>
      {info && (
        <span className={`tm-tag ${info.tone}`} title={info.help}>
          {info.label}
        </span>
      )}
    </span>
  );
}

/* ------------------------------------------------------------------ groups */

const STATUS: Record<WalletGroup['status'], { label: string; cls: string }> = {
  accumulating: { label: 'Buying', cls: 'good' },
  holding: { label: 'Holding', cls: '' },
  distributing: { label: 'Selling', cls: 'bad' },
  exited: { label: 'Sold out', cls: 'muted' },
};

/** Status chip: an emptied group says how it left (sold vs transferred out). */
function statusOf(g: WalletGroup): { label: string; cls: string } {
  if (g.status === 'distributing') return STATUS.distributing;
  const s = groupPosition(g).state;
  if (s === 'sold') return { label: 'Sold out', cls: 'muted' };
  if (s === 'moved' || s === 'mixed') return { label: 'Moved out', cls: 'warn' };
  if (s === 'reduced' && g.status !== 'accumulating') return { label: 'Reduced', cls: '' };
  return STATUS[g.status === 'exited' ? 'holding' : g.status];
}

const RANK: Record<GroupRiskLevel, number> = { high: 0, medium: 1, low: 2 };

const WHY: Record<WalletGroup['kind'], string> = {
  dev_network: 'The creator and wallets it funded',
  bundle: 'Bought together in the same block',
  funder: 'Funded by the same wallet',
  funding_tree: 'Money traces back to one source',
  direct_funding: 'One trader funded the others',
  distribution: 'Got tokens handed out directly',
  cex_batch: 'Same exchange, same amounts, minutes apart',
  wallet_farm: 'New wallets created in a burst by one funder',
  gas_sponsored: 'One relayer paid their first fees (weaker link)',
};

export function GroupList({ onShowOnMap }: { onShowOnMap?: () => void }) {
  const { analysis, selectedGroupId, selectGroup, openWallet } = useTerminal();
  if (!analysis) return <div className="tm-empty">Looking for wallets that belong together…</div>;
  const groups = [...analysis.groups].sort((a, b) => b.stats.holdingPercent - a.stats.holdingPercent || b.wallets.length - a.wallets.length);
  if (!groups.length) {
    return (
      <div className="tm-empty">
        {analysis.progress.tracing
          ? 'Still checking who funded whom…'
          : `No linked wallets found among ${analysis.coverage.traced} traders. Each holder looks independent.`}
      </div>
    );
  }
  return (
    <div className="tm-groups">
      <p className="tm-hint">
        Each group is wallets very likely run by the same people. <b className="r-high">Red</b> groups can dump on you,{' '}
        <b className="r-med">amber</b> ones are worth watching, <b className="r-low">blue</b> ones are low risk.
      </p>
      {[...groups].sort((a, b) => RANK[groupRisk(a).level] - RANK[groupRisk(b).level] || b.stats.holdingPercent - a.stats.holdingPercent).map((g) => {
        const risk = groupRisk(g);
        const c = risk.color;
        const sel = g.id === selectedGroupId;
        const st = statusOf(g);
        return (
          <div key={g.id} className={`tm-group ${sel ? 'sel' : ''}`} style={{ ['--g' as string]: c }}>
            <button className="tm-group-row" onClick={() => selectGroup(g.id)} aria-expanded={sel}>
              <i className="tm-swatch" />
              <span className="tm-group-main">
                <b>
                  {groupTitle(g)} <em className={`tm-risktag r-${risk.level}`}>{risk.level === 'high' ? 'Flagged' : risk.level === 'medium' ? 'Suspicious' : 'Low risk'}</em>
                </b>
                <span>
                  {g.wallets.length} wallets · {WHY[g.kind]}
                </span>
              </span>
              <span className="tm-group-side">
                <b>{pct(g.stats.holdingPercent)}</b>
                <span className={`tm-status ${st.cls}`}>{st.label}</span>
              </span>
            </button>
            {sel && (
              <div className="tm-group-body">
                {g.exitWarning && <div className="tm-warnline">{g.exitWarning}</div>}
                <ul>
                  {g.evidence.slice(0, 3).map((e, i) => (
                    <li key={i}>{e.detail}</li>
                  ))}
                </ul>
                <div className="tm-group-stats">
                  <span>
                    Bought <b>{fmtSol(g.stats.boughtSol)} SOL</b>
                  </span>
                  <span>
                    Sold <b>{fmtSol(g.stats.soldSol)} SOL</b>
                  </span>
                  <span>
                    Peak <b>{pct(g.stats.peakPercent)}</b>
                  </span>
                </div>
                <div className="tm-wallet-chips">
                  {g.wallets.slice(0, 18).map((w) => (
                    <button key={w} className="mono" onClick={() => openWallet(w)}>
                      {shortAddr(w)}
                    </button>
                  ))}
                  {g.wallets.length > 18 && <span className="faint">+{g.wallets.length - 18} more</span>}
                </div>
                {onShowOnMap && (
                  <button className="tm-btn sm" onClick={onShowOnMap}>
                    <IconMap size={16} /> Show on map
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/* ------------------------------------------------------------------ holders */

export function HolderList() {
  const { holders, selectedGroupId, analysis, openWallet, setHoverWallet } = useTerminal();
  const group = groupById(analysis, selectedGroupId);
  const rows = useMemo(() => {
    let r = holders.filter((h) => h.kind === 'wallet' || h.kind === 'service');
    if (group) {
      const set = new Set(group.wallets);
      r = r.filter((h) => set.has(h.address));
    }
    return r.slice(0, 150);
  }, [holders, group]);
  return (
    <div className="tm-list">
      {group && <div className="tm-filternote">Showing {groupTitle(group)} only</div>}
      <table className="tm-table">
        <thead>
          <tr>
            <th>#</th>
            <th>Wallet</th>
            <th className="r">Holds</th>
            <th className="r">Profit</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((h) => {
            const pnl = h.realizedPnlSol + h.unrealizedPnlSol;
            return (
              <tr key={h.address} onClick={() => openWallet(h.address)} onMouseEnter={() => setHoverWallet(h.address)} onMouseLeave={() => setHoverWallet(undefined)}>
                <td className="faint">{h.rank}</td>
                <td>
                  <WalletName address={h.address} label={h.label} tags={h.tags} groupId={h.groupId} />
                </td>
                <td className="r mono">{fmtPct(h.holdingPercent)}</td>
                <td className={`r mono ${pnl > 0 ? 'buy' : pnl < 0 ? 'sell' : 'faint'}`} title="Realized + unrealized, in SOL">
                  {h.buys || h.sells ? `${fmtSol(pnl, true)}` : '—'}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!rows.length && <div className="tm-empty">{holders.length ? 'No holders in this group right now.' : 'Loading holders…'}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ trades */

type Side = 'all' | 'buy' | 'sell';
const SIZES = [
  { id: 0, label: 'Any' },
  { id: 100, label: '$100+' },
  { id: 1000, label: '$1K+' },
  { id: 10000, label: '$10K+' },
] as const;
const WINDOWS = [
  { id: 0, label: 'All' },
  { id: 300, label: '5m' },
  { id: 3600, label: '1h' },
  { id: 86400, label: '24h' },
] as const;
/** Tags worth a chip in the feed, most telling first. */
const FEED_TAGS: TraderTag[] = ['dev', 'insider', 'launch_bundle', 'coordinated_sell', 'farm_wallet', 'token_recipient', 'sniper', 'shared_funder', 'fresh_wallet', 'batch_funded', 'repeat_buyer', 'cex_funded'];
const WHALE_USD = 10_000;
const isTagged = (t: TerminalTrade) => Boolean(t.groupId || t.tags?.some((x) => FEED_TAGS.includes(x)));
const keyOf = (t: TerminalTrade) => `${t.signature}:${t.idx}`;

export function TradeList() {
  const { trades, tradesError, selectedGroupId, analysis, openWallet, mint, solUsd, holders } = useTerminal();
  const [side, setSide] = useState<Side>('all');
  const [minUsd, setMinUsd] = useState(0);
  const [win, setWin] = useState(0);
  const [tagged, setTagged] = useState(false);
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [deep, setDeep] = useState<{ key: string; rows: TerminalTrade[]; loading: boolean }>();
  const [, tick] = useState(0);
  useEffect(() => {
    const t = setInterval(() => tick((x) => x + 1), 5000);
    return () => clearInterval(t);
  }, []);
  const group = groupById(analysis, selectedGroupId);
  const query = q.trim();
  const qOk = /^[1-9A-HJ-NP-Za-km-z]{3,44}$/.test(query);
  const labels = useMemo(() => new Map(holders.filter((h) => h.label).map((h) => [h.address, h.label!])), [holders]);
  const solPrice = solUsd ?? trades.find((t) => t.valueUsd && t.solAmount)?.valueUsd! / (trades.find((t) => t.valueUsd && t.solAmount)?.solAmount ?? 1);

  // The live feed only keeps the newest few hundred trades. With a filter on,
  // also ask the server, which searches every stored trade of the token.
  const filtering = side !== 'all' || minUsd > 0 || win > 0 || qOk || Boolean(group);
  const serverKey = filtering ? JSON.stringify([mint, side, minUsd, win, qOk ? query : '', group?.id]) : '';
  useEffect(() => {
    if (!serverKey || !mint) return setDeep(undefined);
    let alive = true;
    const run = () =>
      terminalApi
        .trades(mint, {
          limit: 500,
          sync: false,
          side: side === 'all' ? undefined : side,
          minSol: minUsd && solPrice ? minUsd / solPrice : undefined,
          since: win ? Date.now() / 1000 - win : undefined,
          q: qOk && query.length < 32 ? query : undefined,
          wallet: qOk && query.length >= 32 ? [query] : group ? group.wallets.slice(0, 200) : undefined,
        })
        .then((r) => alive && setDeep({ key: serverKey, rows: r.trades, loading: false }))
        .catch(() => alive && setDeep((d) => (d ? { ...d, loading: false } : d)));
    setDeep((d) => ({ key: serverKey, rows: d?.key === serverKey ? d.rows : [], loading: true }));
    const t = setTimeout(run, 250);
    const every = setInterval(run, 10_000);
    return () => {
      alive = false;
      clearTimeout(t);
      clearInterval(every);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverKey]);

  const rows = useMemo(() => {
    const since = win ? Date.now() / 1000 - win : 0;
    const set = group ? new Set(group.wallets) : undefined;
    const ql = query.toLowerCase();
    const match = (t: TerminalTrade) =>
      (side === 'all' || t.side === side) &&
      (!minUsd || (t.valueUsd ?? (solPrice ? t.solAmount * solPrice : 0)) >= minUsd) &&
      (!since || t.ts >= since) &&
      (!set || set.has(t.trader)) &&
      (!query || t.trader.toLowerCase().includes(ql) || (labels.get(t.trader) ?? '').toLowerCase().includes(ql)) &&
      (!tagged || isTagged(t));
    const out = new Map<string, TerminalTrade>();
    for (const t of trades) if (match(t)) out.set(keyOf(t), t);
    if (deep?.key === serverKey) for (const t of deep.rows) if (!out.has(keyOf(t)) && match(t)) out.set(keyOf(t), { ...t, ...(analysis?.wallets[t.trader] ? { groupId: analysis.wallets[t.trader]!.groupId, tags: analysis.wallets[t.trader]!.tags } : {}) });
    return [...out.values()].sort((a, b) => b.ts - a.ts || b.slot - a.slot).slice(0, 300);
  }, [trades, deep, serverKey, side, minUsd, win, group, query, tagged, labels, analysis, solPrice]);

  const sum = useMemo(() => {
    let buy = 0;
    let sell = 0;
    for (const t of rows) {
      const v = t.valueUsd ?? (solPrice ? t.solAmount * solPrice : 0);
      if (t.side === 'buy') buy += v;
      else sell += v;
    }
    return { buy, sell };
  }, [rows, solPrice]);

  const any = filtering || tagged || Boolean(query);
  const activeCount = (side !== 'all' ? 1 : 0) + (minUsd ? 1 : 0) + (win ? 1 : 0) + (tagged ? 1 : 0) + (query ? 1 : 0);
  const clear = () => {
    setSide('all');
    setMinUsd(0);
    setWin(0);
    setTagged(false);
    setQ('');
  };
  const sym = useMapStore((s) => s.data?.token.symbol) ?? '';
  const supply = useMapStore((s) => s.data?.token.totalSupply) ?? 0;

  return (
    <div className="tm-list tm-trades">
      <div className="tf-sum">
        <span className="tf-sum-n">{rows.length >= 300 ? '300+' : rows.length} trades</span>
        <span className="buy">Bought {fmtUsd(sum.buy)}</span>
        <span className="sell">Sold {fmtUsd(sum.sell)}</span>
        {deep?.loading && <span className="spinner" aria-label="Searching" />}
        <button className={`tf-fbtn ${activeCount ? 'on' : ''}`} onClick={() => setOpen(true)} aria-haspopup="dialog" aria-expanded={open}>
          <IconFilter size={14} /> Filter{activeCount ? <b>{activeCount}</b> : null}
        </button>
      </div>
      {(activeCount > 0 || group) && (
        <div className="tf-chips">
          {group && (
            <span className="tf-gchip" style={{ ['--g' as string]: groupTint(group) }}>
              {groupTitle(group)}
            </span>
          )}
          {query && (
            <Chip onClear={() => setQ('')}>
              Wallet <b className="mono">{query.length > 12 ? shortAddr(query) : query}</b>
            </Chip>
          )}
          {side !== 'all' && <Chip onClear={() => setSide('all')}>{side === 'buy' ? 'Buys' : 'Sells'}</Chip>}
          {minUsd > 0 && <Chip onClear={() => setMinUsd(0)}>{SIZES.find((x) => x.id === minUsd)?.label}</Chip>}
          {win > 0 && <Chip onClear={() => setWin(0)}>Last {WINDOWS.find((x) => x.id === win)?.label}</Chip>}
          {tagged && <Chip onClear={() => setTagged(false)}>Tagged only</Chip>}
          {activeCount > 1 && (
            <button className="tm-linkish" onClick={clear}>
              Clear all
            </button>
          )}
        </div>
      )}

      {open && (
        <TradeFilterModal
          initial={{ q, side, minUsd, win, tagged }}
          onClose={() => setOpen(false)}
          onApply={(f) => {
            setQ(f.q);
            setSide(f.side);
            setMinUsd(f.minUsd);
            setWin(f.win);
            setTagged(f.tagged);
            setOpen(false);
          }}
        />
      )}

      <ul className="tf-list">
        {rows.map((t) => {
          const usd = t.valueUsd ?? (solPrice ? t.solAmount * solPrice : undefined);
          const g = groupById(analysis, t.groupId);
          const tags = FEED_TAGS.filter((x) => t.tags?.includes(x)).slice(0, 3);
          const whale = (usd ?? 0) >= WHALE_USD;
          const label = labels.get(t.trader);
          return (
            <li key={keyOf(t)} className={`tf-item ${t.side} ${whale ? 'whale' : ''} ${g || tags.length ? 'flag' : ''}`} style={g ? { ['--g' as string]: groupTint(g) } : undefined}>
              <button className="tf-main" onClick={() => openWallet(t.trader)}>
                <span className={`tf-side ${t.side}`}>{t.side === 'buy' ? 'Buy' : 'Sell'}</span>
                <span className="tf-who">
                  <span className="tf-addr">
                    <i className="tm-dot" style={{ background: g ? groupTint(g) : 'var(--bubble-solo)' }} />
                    <span className={label ? '' : 'mono'}>{label ?? shortAddr(t.trader)}</span>
                    {whale && <span className="tm-tag info">Whale</span>}
                    {g && <span className="tm-tag grp">{groupTitle(g)}</span>}
                    {tags.map((x) => (
                      <span key={x} className={`tm-tag ${TRADER_TAG_INFO[x].tone}`} title={TRADER_TAG_INFO[x].help}>
                        {TRADER_TAG_INFO[x].label}
                      </span>
                    ))}
                  </span>
                  <span className="tf-meta">
                    {fmtNum(t.tokenAmount)} {sym}
                    {t.priceUsd && supply ? <> · MC {fmtUsd(t.priceUsd * supply)}</> : null}
                  </span>
                </span>
                <span className="tf-val">
                  <b className={t.side}>{usd !== undefined ? fmtUsd(usd) : `${fmtSol(t.solAmount)} SOL`}</b>
                  <span className="tf-meta">{usd !== undefined ? `${fmtSol(t.solAmount)} SOL` : ''}</span>
                </span>
              </button>
              <span className="tf-side-col">
                <a href={solscanTx(t.signature)} target="_blank" rel="noreferrer" className="tf-age" title={new Date(t.ts * 1000).toLocaleString()}>
                  {fmtAge(t.ts)}
                </a>
                <button className="tf-only" onClick={() => setQ(t.trader)} title="Show only this wallet's trades" aria-label="Filter to this wallet">
                  <IconFilter size={13} />
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      {!rows.length && (
        <div className="tm-empty">
          {tradesError
            ? 'Live trades are unavailable right now.'
            : deep?.loading
              ? 'Searching all trades…'
              : trades.length
                ? any
                  ? 'No trades match these filters.'
                  : 'No trades yet.'
                : 'Loading recent trades…'}
        </div>
      )}
    </div>
  );
}

function Chip({ children, onClear }: { children: ReactNode; onClear: () => void }) {
  return (
    <span className="tf-chip">
      {children}
      <button onClick={onClear} aria-label="Remove filter">
        <IconClose size={11} />
      </button>
    </span>
  );
}

interface TradeFilters {
  q: string;
  side: Side;
  minUsd: number;
  win: number;
  tagged: boolean;
}

/** Trade filters in a small dialog: edit freely, Apply commits, Esc / outside click cancels. */
function TradeFilterModal({ initial, onApply, onClose }: { initial: TradeFilters; onApply: (f: TradeFilters) => void; onClose: () => void }) {
  const [f, setF] = useState(initial);
  const set = <K extends keyof TradeFilters>(k: K, v: TradeFilters[K]) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div className="tf-modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="tf-modal" role="dialog" aria-modal="true" aria-label="Filter trades">
        <div className="tf-modal-head">
          <b>Filter trades</b>
          <button className="tm-iconbtn" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            onApply({ ...f, q: f.q.trim() });
          }}
        >
          <label className="tf-field">
            <span>Wallet</span>
            <span className="tf-search">
              <IconSearch size={15} />
              <input value={f.q} onChange={(e) => set('q', e.target.value)} placeholder="Part of a wallet address" spellCheck={false} autoFocus />
              {f.q && (
                <button type="button" onClick={() => set('q', '')} aria-label="Clear wallet">
                  <IconClose size={13} />
                </button>
              )}
            </span>
          </label>
          <div className="tf-field">
            <span>Side</span>
            <Seg value={f.side} onChange={(v) => set('side', v)} items={[['all', 'All'], ['buy', 'Buys'], ['sell', 'Sells']]} label="Side" />
          </div>
          <div className="tf-field">
            <span>Trade size</span>
            <Seg value={f.minUsd} onChange={(v) => set('minUsd', v)} items={SIZES.map((x) => [x.id, x.label])} label="Size" />
          </div>
          <div className="tf-field">
            <span>Time</span>
            <Seg value={f.win} onChange={(v) => set('win', v)} items={WINDOWS.map((x) => [x.id, x.label])} label="Time" />
          </div>
          <label className="tf-check">
            <input type="checkbox" checked={f.tagged} onChange={(e) => set('tagged', e.target.checked)} />
            <span>
              <b>Tagged wallets only</b>
              <em>Dev, insiders, bundles, snipers, farms and linked groups</em>
            </span>
          </label>
          <div className="tf-modal-foot">
            <button type="button" className="tm-btn" onClick={() => setF({ q: '', side: 'all', minUsd: 0, win: 0, tagged: false })}>
              Reset
            </button>
            <button type="submit" className="tm-btn primary">
              Apply
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Seg<T extends string | number>({ value, onChange, items, label }: { value: T; onChange: (v: T) => void; items: (readonly [T, string])[]; label: string }) {
  return (
    <div className="tf-seg" role="radiogroup" aria-label={label}>
      <em>{label}</em>
      {items.map(([v, l]) => (
        <button key={String(v)} type="button" role="radio" aria-checked={value === v} className={value === v ? 'on' : ''} onClick={() => onChange(v)}>
          {l}
        </button>
      ))}
    </div>
  );
}
